import React, { useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Angry } from 'lucide-react';
import { getClassLevel } from '../../../shared/rules/classIdentity.mjs';
import { hasBerserkerFrenzy } from '../../../shared/rules/berserkerFrenzy.mjs';
import { mergeRage, rageProfile } from '../../../shared/rules/barbarianRage.mjs';
import { areHostile } from '../../../shared/creatureDisposition';
import { minimumFootprintDistanceFeet } from '../utils/pushGeometryCore.mjs';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';

const RANGE_FEET = 30;

/**
 * Intimidating Presence (Berserker 14): a Bonus Action tile. Choose creatures within 30 feet; the server rolls their
 * Wisdom saves and frightens those that fail. Once per Long Rest, or restored by expending a use of Rage.
 */
export default function IntimidatingPresenceControls({ form, characterId, combatState, spatialContext, isActiveTurn, onCharacterChange, onCombatStateReconciled }) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const actorId = String(characterId);
  const eligible = hasBerserkerFrenzy(form) && getClassLevel(form, 'barbarian') >= 14;
  const used = Boolean(form?.classState?.barbarian?.intimidatingPresence?.used);
  const rage = rageProfile(form);
  const inRange = useMemo(() => {
    const tokens = spatialContext?.tokens || {}, entities = spatialContext?.entities || {};
    if (!tokens[actorId]) return [];
    return (combatState?.participants || []).map((p) => String(p.characterId || p.enemyId)).filter((id) => id && id !== actorId && tokens[id]).map((id) => {
      const entity = entities[id] || {};
      const distance = minimumFootprintDistanceFeet({ sourceToken: tokens[actorId], targetToken: tokens[id], sourceSize: form?.size, targetSize: entity.size, map: spatialContext?.map });
      return { id, name: entity.label || entity.name || id, distance, hostile: areHostile({ ...form, enemy: false }, entity) };
    }).filter((entry) => entry.distance !== null && entry.distance <= RANGE_FEET);
  }, [actorId, combatState?.participants, form, spatialContext]);
  if (!eligible) return null;
  const start = () => { setChosen(inRange.filter((entry) => entry.hostile).map((entry) => entry.id)); setOpen(true); };
  const toggle = (id) => setChosen((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  const submit = async () => {
    if (lock.current || !chosen.length) return;
    lock.current = true; setBusy(true);
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(String(form?._id || characterId))}/barbarian/intimidating-presence`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: window.crypto?.randomUUID?.() || `ip-${Date.now()}`, targetIds: chosen, turnSequence: combatState?.turnSequence, expendRage: used }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Intimidating Presence is unavailable.');
      onCombatStateReconciled?.(result.combat);
      onCharacterChange?.((previous) => {
        const next = { ...previous, classState: { ...previous.classState, barbarian: { ...previous.classState?.barbarian, intimidatingPresence: { used: true } } } };
        return result.rage ? mergeRage(next, result.rage) : next;
      });
      const frightened = result.results.filter((entry) => entry.success === false).map((entry) => entry.name);
      notify(`Intimidating Presence (DC ${result.dc}): ${frightened.length ? `${frightened.join(', ')} ${frightened.length === 1 ? 'is' : 'are'} Frightened` : 'every creature resisted'}.`, frightened.length ? 'success' : 'info');
      setOpen(false);
    } catch (error) { notify(error.message || 'Intimidating Presence is unavailable.', 'danger'); }
    finally { lock.current = false; setBusy(false); }
  };
  const ledger = combatState?.turnActionLedger?.combatantId === actorId && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const blocked = !isActiveTurn || ledger?.bonusActionSpent || (used && rage.current < 1);
  return (
    <>
      <button type="button" data-action-tile="intimidating-presence" className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{ '--hud-resource-accent': '#a855f7' }}
        aria-label="Intimidating Presence" disabled={busy || blocked} onClick={start}
        title={used ? (rage.current > 0 ? 'Spent until a Long Rest · expend a Rage use to restore it · Bonus Action' : 'Spent until a Long Rest') : 'Bonus Action · Wisdom save or Frightened, 30-foot Emanation'}>
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Angry size={20} /></span>
        <span className="combat-hud-resource-tile__name"><span className="combat-hud-resource-tile__name-line">Intimidating</span><span className="combat-hud-resource-tile__name-line">Presence</span></span>
        <span className="combat-hud-resource-tile__value">{used ? 'Used' : 'Ready'}</span>
      </button>
      <Modal centered show={open} onHide={() => !busy && setOpen(false)} className="dnd-modal modern-modal" aria-label="Intimidating Presence">
        <Modal.Header closeButton={!busy}><Modal.Title>Intimidating Presence</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Choose creatures within 30 feet. Each makes a Wisdom saving throw or is Frightened of you for 1 minute, repeating the save at the end of each of its turns.</p>
          {used && <p className="text-warning">Already used since your last Long Rest: this expends one of your {rage.current} Rage uses to restore it.</p>}
          {inRange.length === 0 ? <p>No creatures are within 30 feet on the map.</p> : inRange.map((entry) => (
            <Form.Check key={entry.id} id={`ip-${entry.id}`} label={`${entry.name} · ${entry.distance} ft`} checked={chosen.includes(entry.id)} onChange={() => toggle(entry.id)} />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy || !chosen.length} onClick={submit}>{busy ? 'Rolling saves…' : `Frighten ${chosen.length || ''}`.trim()}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
